/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Product Offerings Modal Success component
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.16.0
 * @since 1.10.0
 */

import { useCallback } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Button, LinkBlock } from '../../../components/common';
import { appHistoryPush } from '../../../modules/app/appHistory';
import {
    makeSelectTranslationWizardProductOfferings,
    makeSelectWizardTranslation,
} from '../../../modules/internationalization/intSelectors';
import { IWizardProductOfferingsTranslation } from '../../../modules/internationalization/types';
import { IWizardTranslation } from '../../../modules/internationalization/types/wizards/wizard';
import { IModalPopup } from '../../../modules/modal/components/ModalPopup';
import {
    ModalPopupContainerStyled,
    ModalPopupControlContainer,
    ModalPopupTextStyled,
    ModalPopupTitleStyled,
} from '../../../modules/modal/components/styled/ModalStyled';
import { guardComponentType } from '../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../modules/role/selectors/guardSelector';
import { IRootReducer } from '../../../reducers/rootReducer';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { getPageInquiryInfoUrl, isPageInquiryInfoExternalUrl } from '../../PageInquiryInfo/utils/getInquiryInfoUrl';

interface IProductOfferingsModalSuccessInquiryProps {
    goBack: () => void;
    campaignInquiryId: string;
}

type TProductOfferingsModalSuccessInquiry = IProductOfferingsModalSuccessInquiryProps & IModalPopup & IConnectedProps;

const ProductOfferingsModalSuccessInquiry = (props: TProductOfferingsModalSuccessInquiry) => {

    const {
        componentsFeature,
        goBack,
        onCloseModal,
        campaignInquiryId,
        wizardProductOfferingsTranslation,
        wizardTranslation,
    } = props;

    const url = getPageInquiryInfoUrl(componentsFeature, campaignInquiryId);

    const onButtonClick = useCallback(() => {
        onCloseModal();

        appHistoryPush(RoutePath.main);
    }, [onCloseModal, goBack]);

    return (
        <ModalPopupContainerStyled
            data-component={'modal-popup'}
            data-name={props.dataName}
            width="490px">
            <ModalPopupTitleStyled data-name="modal-popup-title">
                {wizardProductOfferingsTranslation.wizardModalSuccess.inquiryMessageStart}&nbsp;
                <LinkBlock
                    isExternalUrl={isPageInquiryInfoExternalUrl(componentsFeature)}
                    url={url}
                >
                    № {campaignInquiryId}
                </LinkBlock>
                &nbsp;
                {wizardProductOfferingsTranslation.wizardModalSuccess.inquiryMessageEnd}
            </ModalPopupTitleStyled>
            <ModalPopupTextStyled data-name="modal-popup-text">
                {wizardTranslation.wizardModalSummary.details} &nbsp;
                <LinkBlock url={RoutePath.inquiries} onClick={onCloseModal}>
                    {wizardProductOfferingsTranslation.wizardModalSuccess.inquiryPage}
                </LinkBlock>
            </ModalPopupTextStyled>
            <ModalPopupControlContainer>
                <Button dataName="close-button" onClick={onButtonClick} onMouseDown={onButtonClick}>
                    {wizardProductOfferingsTranslation.wizardModalSuccess.mainPage}
                </Button>
            </ModalPopupControlContainer>
        </ModalPopupContainerStyled>
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    wizardProductOfferingsTranslation: IWizardProductOfferingsTranslation;
    wizardTranslation: IWizardTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    wizardProductOfferingsTranslation: makeSelectTranslationWizardProductOfferings(),
    wizardTranslation: makeSelectWizardTranslation(),
});

export default connect(mapStateToProps)(ProductOfferingsModalSuccessInquiry);
